/**
 * Sales notification popup — 100% REAL order data.
 *
 * Vanilla JS, no dependencies. One instance runs sitewide.
 *
 * Data: the section inlines shop.metafields.social_proof.recent_orders
 * (written every 5 minutes by scripts/refresh-sales-popup.mjs from actual
 * orders). Records are privacy-reduced — first name + product + a coarse
 * "today / yesterday / this week / recently" bucket. There is no fabricated
 * fallback pool: when nothing is selling right now, the rotation simply
 * continues through this week's real purchases.
 *
 * Live celebration: the script polls the always-online feed API (section
 * setting "Live feed URL", a Vercel function that builds the feed from real
 * orders on demand; falls back to re-rendering this section via the Section
 * Rendering API when the URL is blank). The feed
 * carries a `fresh_key` while its newest order is only minutes old; the
 * first time a browser sees an uncelebrated fresh_key it fires a confetti
 * drop from the top of the page and shows that sale's popup immediately at
 * the bottom. Celebrated keys persist in localStorage so each sale
 * celebrates exactly once per browser; every record shown this session is
 * tracked in sessionStorage so nothing repeats across page navigations.
 */

const SESSION_SHOWN_KEYS = 'salesPopup:shownKeys';
const CELEBRATED_KEYS = 'salesPopup:celebrated';
const CELEBRATED_CAP = 50;

class SalesPopup {
  constructor(root) {
    this.root = root;
    this.config = this.parseConfig(root.dataset);
    this.shownKeys = this.loadJson(sessionStorage, SESSION_SHOWN_KEYS, []);
    this.celebrated = this.loadJson(localStorage, CELEBRATED_KEYS, []);
    this.shownCount = 0;
    this.queue = [];
    this.knownKeys = new Set();
    this.timers = [];
    this.destroyed = false;
    this.celebrating = false;

    this.init();
  }

  parseConfig(d) {
    return {
      currentHandle: d.currentHandle || null,
      enableDesktop: d.enableDesktop === 'true',
      enableMobile: d.enableMobile === 'true',
      delay: parseInt(d.delay, 10) || 5000,
      duration: parseInt(d.duration, 10) || 6000,
      interval: parseInt(d.interval, 10) || 8000,
      speed: parseInt(d.speed, 10) || 400,
      maxPopups: parseInt(d.maxPopups, 10) || 0, // 0 = unlimited
      randomOrder: d.randomOrder === 'true',
      showAvatar: d.showAvatar === 'true',
      showTime: d.showTime === 'true',
      showVerified: d.showVerified === 'true',
      verifiedText: d.verifiedText || 'Verified Purchase',
      purchasedText: d.purchasedText || 'purchased',
      enableLink: d.enableLink === 'true',
      labels: {
        'today': d.labelToday || 'today',
        'yesterday': d.labelYesterday || 'yesterday',
        'this week': d.labelWeek || 'this week',
        'recently': d.labelRecently || 'recently',
      },
      enableLive: d.enableLive === 'true',
      feedUrl: d.feedUrl || '',
      enableConfetti: d.enableConfetti === 'true',
      confettiIntensity: d.confettiIntensity || 'festive',
      pollInterval: Math.max(30000, parseInt(d.pollInterval, 10) || 60000),
      freshBadgeText: d.freshBadgeText || '🎉 Just now',
    };
  }

  loadJson(storage, key, fallback) {
    try {
      return new Set(JSON.parse(storage.getItem(key) || JSON.stringify(fallback)));
    } catch (e) {
      return new Set(fallback);
    }
  }

  saveJson(storage, key, set, cap) {
    try {
      let arr = [...set];
      if (cap && arr.length > cap) arr = arr.slice(arr.length - cap);
      storage.setItem(key, JSON.stringify(arr));
    } catch (e) {
      // Storage can be unavailable (private mode, quota) — in-memory sets
      // still prevent repeats for this page view.
    }
  }

  markShown(key) {
    this.shownKeys.add(key);
    this.saveJson(sessionStorage, SESSION_SHOWN_KEYS, this.shownKeys);
  }

  markCelebrated(key) {
    this.celebrated.add(key);
    this.saveJson(localStorage, CELEBRATED_KEYS, this.celebrated, CELEBRATED_CAP);
  }

  init() {
    if (!this.shouldRunOnThisViewport()) return;

    const feed = this.parseInlineFeed();
    this.absorbFeed(feed);

    if (this.config.randomOrder) this.queue = this.shuffle(this.queue);

    // A sale that happened minutes ago and hasn't been celebrated in this
    // browser yet celebrates right away — even on a fresh page load.
    const freshEntry = this.pendingFreshEntry(feed);
    this.pendingFreshKey = freshEntry ? freshEntry.key : null;
    this.rotationStarted = Boolean(freshEntry || this.queue.length);
    if (freshEntry) {
      this.timers.push(setTimeout(() => this.celebrate(freshEntry), Math.min(this.config.delay, 2000)));
    } else if (this.queue.length) {
      this.timers.push(setTimeout(() => this.showNext(), this.config.delay));
    }

    // The inline copy is only as fresh as the last metafield sync — pull the
    // always-online feed right away so a brand-new sale never waits for the
    // first poll tick.
    if (this.config.feedUrl) this.fetchRemoteFeed().then((feed2) => feed2 && this.handleFeed(feed2));

    if (this.config.enableLive) this.schedulePoll();
  }

  shouldRunOnThisViewport() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return isMobile ? this.config.enableMobile : this.config.enableDesktop;
  }

  parseInlineFeed() {
    const node = this.root.querySelector('[data-sales-popup-feed]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /** Convert feed records to queue entries; new keys are prepended so the
   *  newest purchases always surface first. Returns the entries added. */
  absorbFeed(feed) {
    if (!feed || !Array.isArray(feed.records)) return [];
    const fresh = [];
    for (const record of feed.records) {
      if (!record || !record.key || this.knownKeys.has(record.key)) continue;
      if (!record.product_title || !record.product_url) continue;
      if (record.product_url === this.config.currentHandle) continue;
      this.knownKeys.add(record.key);
      fresh.push({
        key: record.key,
        name: record.name || 'Someone',
        timeLabel: this.config.labels[record.time_bucket] || this.config.labels.recently,
        product: {
          title: record.product_title,
          url: record.product_url,
          image: record.product_image,
        },
      });
    }
    // Feed is newest-first; keep that ordering when prepending.
    this.queue = fresh.concat(this.queue);
    return fresh;
  }

  pendingFreshEntry(feed) {
    if (!feed || !feed.fresh_key) return null;
    if (!this.config.enableLive) return null;
    if (this.celebrated.has(feed.fresh_key)) return null;
    if (this.pendingFreshKey === feed.fresh_key) return null; // already scheduled
    return this.queue.find((e) => e.key === feed.fresh_key) || null;
  }

  shuffle(array) {
    const copy = array.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  /* ---- Live polling ----------------------------------------------------- */

  schedulePoll() {
    if (this.destroyed) return;
    this.timers.push(setTimeout(() => this.poll(), this.config.pollInterval));
  }

  async poll() {
    if (this.destroyed) return;
    if (document.hidden) {
      this.schedulePoll();
      return;
    }
    const feed = this.config.feedUrl ? await this.fetchRemoteFeed() : await this.fetchSectionFeed();
    if (feed) this.handleFeed(feed);
    this.schedulePoll();
  }

  /** Always-online feed API (Vercel) — primary live source. */
  async fetchRemoteFeed() {
    try {
      const res = await fetch(this.config.feedUrl);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null; // network hiccups are fine — next poll retries
    }
  }

  /** Fallback live source: re-render this section and read its inline feed. */
  async fetchSectionFeed() {
    try {
      const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${rootPath}?sections=sales-popup`);
      if (!res.ok) return null;
      const html = (await res.json())['sales-popup'];
      if (!html) return null;
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sales-popup-feed]');
      return node ? JSON.parse(node.textContent) : null;
    } catch (e) {
      return null;
    }
  }

  handleFeed(feed) {
    if (this.destroyed) return;
    this.absorbFeed(feed);
    const freshEntry = this.pendingFreshEntry(feed);
    if (freshEntry && !this.celebrating) {
      this.pendingFreshKey = freshEntry.key;
      this.rotationStarted = true;
      this.celebrate(freshEntry);
    } else if (!this.rotationStarted && this.queue.length) {
      // Store had no showable orders at page load; first records just
      // arrived — start the rotation now.
      this.rotationStarted = true;
      this.timers.push(setTimeout(() => this.showNext(), this.config.delay));
    }
  }

  /* ---- New-sale celebration --------------------------------------------- */

  celebrate(entry) {
    if (this.destroyed) return;
    this.markCelebrated(entry.key);
    this.celebrating = true;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.config.enableConfetti && !reducedMotion) this.dropConfetti();

    // The fresh sale jumps the queue: clear pending rotation timers and show
    // it now, flagged with the "just now" badge.
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
    this.showEntry(entry, { fresh: true });
    if (this.config.enableLive) this.schedulePoll();
  }

  dropConfetti() {
    const accent =
      getComputedStyle(this.root).getPropertyValue('--sp-accent').trim() || '#E8590C';
    const badge =
      getComputedStyle(this.root).getPropertyValue('--sp-badge-bg').trim() || '#FFD43B';
    const palette = [accent, badge, '#F6C453', '#F9E2AE', '#FFFFFF', '#F4A9A0'];
    const counts = { subtle: 70, festive: 130, max: 200 };
    const count = counts[this.config.confettiIntensity] || 130;

    const canvas = document.createElement('canvas');
    canvas.className = 'sales-popup-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Pieces rain from just above the viewport, staggered so the drop reads
    // as a graceful cascade rather than a single clump.
    const pieces = [];
    for (let i = 0; i < count; i++) {
      const shape = Math.random() < 0.7 ? 'rect' : 'circle';
      pieces.push({
        x: Math.random() * w,
        y: -20 - Math.random() * h * 0.6,
        size: 5 + Math.random() * 6,
        color: palette[Math.floor(Math.random() * palette.length)],
        vy: 1.6 + Math.random() * 2.2,
        sway: 0.6 + Math.random() * 1.6,
        phase: Math.random() * Math.PI * 2,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.2,
        shape,
      });
    }

    const duration = 3600;
    const start = performance.now();
    let raf;

    const frame = (now) => {
      const t = now - start;
      ctx.clearRect(0, 0, w, h);
      const fade = t > duration - 700 ? Math.max(0, (duration - t) / 700) : 1;

      for (const p of pieces) {
        p.y += p.vy;
        p.x += Math.sin(t / 480 + p.phase) * p.sway * 0.6;
        p.rot += p.vr;
        if (p.y > h + 20) continue;

        ctx.save();
        ctx.globalAlpha = fade * 0.95;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') {
          // Vertical squish fakes the 3-D tumble of a paper strip.
          const squish = Math.abs(Math.sin(t / 300 + p.phase));
          ctx.fillRect(-p.size / 2, (-p.size * 0.6 * squish) / 2, p.size, Math.max(1.5, p.size * 0.6 * squish));
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2.4, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (t < duration && !this.destroyed) {
        raf = requestAnimationFrame(frame);
      } else {
        cancelAnimationFrame(raf);
        canvas.remove();
      }
    };
    raf = requestAnimationFrame(frame);
  }

  /* ---- Rotation ---------------------------------------------------------- */

  /** True while a menu drawer, cart drawer, or modal is open — Dawn marks
   *  this by adding one of these classes to <body>. We just wait rather
   *  than skip, so the cycle resumes right after the customer is done. */
  isInterfaceBusy() {
    const body = document.body.classList;
    return (
      body.contains('overflow-hidden') ||
      body.contains('overflow-hidden-mobile') ||
      body.contains('overflow-hidden-tablet') ||
      body.contains('overflow-hidden-desktop')
    );
  }

  pickNext() {
    const unshown = this.queue.filter((entry) => !this.shownKeys.has(entry.key));
    if (unshown.length) return unshown[0];
    // Everything real has been shown once this session; start a second pass
    // instead of going silent — these are still genuine purchases.
    if (this.queue.length) return this.queue[this.shownCount % this.queue.length];
    return null;
  }

  showNext() {
    if (this.destroyed) return;
    if (this.config.maxPopups > 0 && this.shownCount >= this.config.maxPopups) return;

    if (this.isInterfaceBusy()) {
      this.timers.push(setTimeout(() => this.showNext(), 1000));
      return;
    }

    const entry = this.pickNext();
    if (!entry) return;
    this.showEntry(entry, { fresh: false });
  }

  showEntry(entry, { fresh }) {
    this.render(entry, fresh);
    this.markShown(entry.key);
    this.shownCount++;
    this.root.hidden = false;

    const card = this.root.querySelector('.sales-popup__card');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => card && card.classList.add('is-visible'));
    });

    // A fresh-sale card earns a little more screen time.
    const duration = fresh ? this.config.duration + 2500 : this.config.duration;
    this.timers.push(setTimeout(() => this.hideCurrent(), duration));
  }

  hideCurrent() {
    const card = this.root.querySelector('.sales-popup__card');
    if (card) card.classList.remove('is-visible');
    this.celebrating = false;

    this.timers.push(
      setTimeout(() => {
        this.root.hidden = true;
        this.scheduleNext();
      }, this.config.speed)
    );
  }

  scheduleNext() {
    if (this.destroyed) return;
    if (this.config.maxPopups > 0 && this.shownCount >= this.config.maxPopups) return;
    this.timers.push(setTimeout(() => this.showNext(), this.config.interval));
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  render(entry, fresh) {
    const name = this.escapeHtml(entry.name);
    const title = this.escapeHtml(entry.product.title);
    const image = this.escapeHtml(entry.product.image);
    const url = this.escapeHtml(entry.product.url);
    const purchased = this.escapeHtml(this.config.purchasedText);

    const badge = fresh
      ? `<div class="sales-popup__badge">${this.escapeHtml(this.config.freshBadgeText)}</div>`
      : '';

    const avatar = this.config.showAvatar && image
      ? `<div class="sales-popup__avatar"><img src="${image}" alt="" loading="lazy" width="88" height="88"></div>`
      : '';

    const metaParts = [];
    if (this.config.showVerified) {
      metaParts.push(`<span class="sales-popup__verified">${this.escapeHtml(this.config.verifiedText)}</span>`);
    }
    if (this.config.showTime && !fresh) {
      metaParts.push(`<span class="sales-popup__time">${this.escapeHtml(entry.timeLabel)}</span>`);
    }
    const meta = metaParts.length
      ? `<div class="sales-popup__meta">${metaParts.join('<span class="sales-popup__meta-divider">&middot;</span>')}</div>`
      : '';

    const cardInner = `
      <button type="button" class="sales-popup__close" aria-label="Dismiss">&times;</button>
      ${avatar}
      <div class="sales-popup__content">
        <p class="sales-popup__name">${name}</p>
        <p class="sales-popup__product-line">${purchased} <span class="sales-popup__product-name">${title}</span></p>
        ${meta}
      </div>
    `;

    const cardTag = this.config.enableLink ? 'a' : 'div';
    const cardHref = this.config.enableLink ? `href="${url}"` : '';
    const freshClass = fresh ? ' sales-popup__card--fresh' : '';

    this.root.innerHTML = `
      ${badge}
      <${cardTag} class="sales-popup__card${freshClass}" ${cardHref}>${cardInner}</${cardTag}>
    `;

    const closeBtn = this.root.querySelector('.sales-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.hideCurrent();
      });
    }
  }

  destroy() {
    this.destroyed = true;
    this.timers.forEach((t) => clearTimeout(t));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const root = document.querySelector('[data-sales-popup-root]');
  if (root) new SalesPopup(root);
});
